// IOSInstallBanner
// Sticky, persistent banner shown when iOS users are in a Safari tab
// (not standalone). iOS web push is hard-gated on the app being
// installed to Home Screen — without that, every push silently fails.
// This banner is the only fix; users can dismiss it for the session
// but it returns on next load until they install.

import { useEffect, useState } from 'react';
import { Share, Plus, X } from 'lucide-react';
import { needsIOSHomeScreenInstall } from '@/lib/platform';
import { isPWAInstallModalOpen, subscribePWAInstallModal } from '@/lib/pwaInstallModal';

const SESSION_DISMISS_KEY = 'ios_install_banner_dismissed_session';

export default function IOSInstallBanner() {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(isPWAInstallModalOpen);

  useEffect(() => {
    if (!needsIOSHomeScreenInstall()) {
      setShow(false);
      return;
    }
    // Per-session dismiss only (no localStorage). On a new app launch,
    // the banner shows again — this state is critical enough that we
    // don't let users hide it forever.
    if (sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') {
      setShow(false);
      return;
    }
    setShow(true);
  }, []);

  // Step aside while the PWAInstallPrompt hero modal is up — same install
  // CTA, no need to compete for attention. The banner reappears automatically
  // when the modal closes (e.g. user snoozed for the session).
  useEffect(() => subscribePWAInstallModal(setModalOpen), []);

  if (!show || modalOpen) return null;

  return (
    <div
      className="px-4 py-3 mx-3 mt-2 rounded-xl"
      style={{
        background: 'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(245,158,11,0.06))',
        border: '1px solid rgba(245,158,11,0.35)',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
          <Share className="w-4 h-4 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black uppercase tracking-wider text-white">
            Install to Home Screen
          </p>
          <p className="text-[11px] text-white/70 mt-0.5 leading-snug">
            iOS only delivers notifications to apps installed to Home Screen.
            Until you install, push will not arrive, even with permission granted.
          </p>
          {expanded && (
            <ol className="mt-2 space-y-1 text-[11px] text-white/85 list-decimal pl-4">
              <li>Tap the <Share className="w-3 h-3 inline mb-0.5" /> Share icon at the bottom of Safari.</li>
              <li>Scroll down and tap <Plus className="w-3 h-3 inline mb-0.5" /> "Add to Home Screen".</li>
              <li>Open The Standard from the Home Screen icon (not Safari).</li>
              <li>Re-enable notifications inside the installed app.</li>
            </ol>
          )}
        </div>
        <button
          onClick={() => { sessionStorage.setItem(SESSION_DISMISS_KEY, '1'); setShow(false); }}
          className="shrink-0 text-white/40 hover:text-white/70 active:opacity-60 p-1"
          aria-label="Dismiss"
          title="Dismiss for this session"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="mt-2 px-3 py-1.5 rounded-lg bg-amber-500 text-black text-xs font-black uppercase tracking-wider active:opacity-70"
      >
        {expanded ? 'Hide steps' : 'Show me how'}
      </button>
    </div>
  );
}
